import { ORM } from 'redux-orm';
import { hasOwnProperty } from 'utils/objects';
import { EnumObject } from 'utils/tsUtils';

import { BatchedUpdates } from 'enums';
import Contact from './Contact';
import Company from './Company';
import Interaction from './Interaction';
import Score from './Score';
import Portfolio from './Portfolio';
import Reminder from './Reminder';
import News from './News';
import Nba from './Nba';
import User from './User';
import Field from './Field';
import Suggested from './Suggested';
import PlannedInteraction from './PlannedInteraction';
import Integration from './Integration';
import View from './View';
import { ORMSchema, ORMState, ORMSession } from './externalTypes';

const orm = new ORM<ORMSchema>();

export type Models = typeof orm;

orm.register(
  Contact,
  Company,
  Interaction,
  Score,
  Portfolio,
  Reminder,
  News,
  Nba,
  User,
  Field,
  PlannedInteraction,
  Suggested,
  Integration,
  View,
);

/**
 * Manages the creation of redux-orm Session objects
 * for use in ORM state updates.
 *
 * Allows for the persistance of a session across
 * redux action dispatches to allow for
 * batched ORM update processing.
 *
 * Without this persistence - the batched update
 * may have stale state and newer state updates
 * will be over written.
 * 
 * Tradeoff is that other redux state updates
 * will cause ORM updates while a batched process
 * is active - causing re-renders.
 * 
 * @returns - ORM Session Manager instance:
 * {
 *  @param getAlternateSession - every time initSession is called
 *    an alternative session is created.  This never persists
 *    across initSession calls.  Usefull when you need to
 *    update ORM state but don't want to push processed batched
 *    values in other models.
 *  @param getBatchedTaskInProgress - given the batchId this will return
 *    whether that batched task is in progress.
    @param getBatchedTasksInProgress - returns true if any batched
      process is processing.
 *  @param getSession - gets the current session.  This session
 *    will persist while batching is occuring.
    @param initSession - will return the current session if
      a batched task is running, or a new session otherwise and return that.
      Also creates an alternate session.
    @param startBatchUpdate - register a batched ORM update task
      with id.  It will throw if a task with the same id has already
      been registered.  Wrap the call to this function and the
      batch process with try / catch block to prevent unecessary
      batch process repetition.
    @param endBatchUpdate - unregister batched ORM update task by id.
      will throw if id is not registered.
 * }
 */
const createSessionManager = () => {
  let ormState: ORMState;
  let alternateSession: ORMSession;
  let session: ORMSession;
  let batchedTasksInProgress = false;

  const activeBatchedTasks: EnumObject<BatchedUpdates, boolean> = {};

  const initSession = (initState: ORMState) => {
    alternateSession = orm.session(initState);
    if (batchedTasksInProgress) {
      return session;
    }
    ormState = initState;
    session = orm.session(ormState);
    return session;
  };

  const getAlternateSession = () => alternateSession;

  const getSession = () => session;

  const getBatchedTasksInProgress = () => batchedTasksInProgress;

  const getBatchedTaskInProgress = (batchId: BatchedUpdates) =>
    hasOwnProperty(activeBatchedTasks, batchId);

  const startBatchUpdate = (batchId: BatchedUpdates | null = null) => {
    if (batchId === null || hasOwnProperty(activeBatchedTasks, batchId)) {
      /*
        Spamming chained batched calls like refreshData in actions/auth can
        prevent them from effectively preventing batch requests from starting.
        The Sidebar component is a known source of this.  But other sources
        of this - if they show up in logging, should be investigated.
      */
      throw new Error(
        `Can't init batch update - Batched ORM update task id: ${batchId} is null or already active`,
      );
    }
    activeBatchedTasks[batchId] = true;
    batchedTasksInProgress = true;
  };

  const endBatchUpdate = (batchId: BatchedUpdates | null = null) => {
    if (batchId === null || !hasOwnProperty(activeBatchedTasks, batchId)) {
      throw new Error(
        `Can't halt batch update. Batched ORM update task id: ${batchId} is null or not active`,
      );
    }
    delete activeBatchedTasks[batchId];
    if (Object.keys(activeBatchedTasks).length === 0) {
      batchedTasksInProgress = false;
    }
  };

  const manager = {
    getAlternateSession,
    getBatchedTaskInProgress,
    getBatchedTasksInProgress,
    getSession,
    initSession,
    startBatchUpdate,
    endBatchUpdate,
  };

  return manager;
};

export const sessionManager = createSessionManager();

export default orm;
